import { useState } from 'react'
import { ChevronRight, Zap } from 'lucide-react'

import type { TurnPhaseTiming } from '@atbash-labs/ontology-agent-wire'

import { agentShortLabel } from '../../lib/agentLabels'
import { formatCallDurationMs } from '../../lib/format-duration'
import { sanitizeSubgraphSvg } from '../../lib/sanitize-svg'
import { cn } from '../../lib/utils'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { groupConsecutiveByBatchId } from './toolCallBatches'
import { deriveTurnPhases, phaseSegments, type PhaseSegment, type TurnPhases } from './turnPhases'
import { formatPayload } from './formatPayload'

export interface ToolCallRowEntry {
  id: string
  name: string
  input: unknown
  output: unknown
  parentStepIndex: number
  agentKind: string
  depth: number
  status: 'running' | 'completed' | 'error'
  /**
   * Shared by tools that ran in the same parallel group (live overlap) or
   * the same assistant message (persisted history). Missing on flat
   * raw_response snapshots that have no grouping signal.
   */
  batchId?: string
  /** Absent when this surface has no timing. `null` means unknown (show —). */
  durationMs?: number | null
  /** Epoch ms the executor started, so a batch reports wall time instead of a sum. */
  startedAt?: number
  /** Model phases of the generating turn. Identical across the turn's batch. */
  turnTiming?: TurnPhaseTiming
}

export type SidebarToolCallEntry = ToolCallRowEntry

// Both the current cypher-driven render and its legacy label-scoped
// predecessor produce the same { svg, title, ... } shape, so the
// inline-SVG block in the debug tool trace handles either name.
const SUBGRAPH_TOOL_NAMES = new Set<string>(['draw_cypher_subgraph', 'draw_ingest_subgraph'])

interface ToolCallRowProps {
  index: number
  name: string
  input: unknown
  output: unknown
  status?: 'running' | 'completed' | 'error'
  agentKind?: string
  depth?: number
  /** 1-based tool-calling turn this call belongs to. */
  turn?: number
  /** Position within a multi-call turn, as `2/3`. Omitted for lone calls. */
  batchPosition?: string
  durationMs?: number | null
  /** Longest duration in the sibling set, for the relative bar. */
  maxDurationMs?: number
}

/**
 * The debug tool log. Renders one row per call and wraps calls from the same
 * turn in a labelled parallel group, so "did the agent batch anything?" is
 * answerable at a glance without expanding rows.
 */
export function SidebarToolCallList({ calls }: { calls: ToolCallRowEntry[] }) {
  const groups = groupConsecutiveByBatchId(calls)
  const showDuration = calls.some((c) => c.durationMs !== undefined)
  const maxDurationMs = Math.max(
    0,
    ...calls.map((c) => (typeof c.durationMs === 'number' ? c.durationMs : 0))
  )
  let nextIndex = 0
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {groups.map((group, groupIndex) => {
        const startIndex = nextIndex
        nextIndex += group.length
        return (
          <ToolCallGroup
            key={group[0].id}
            calls={group}
            startIndex={startIndex}
            turn={groupIndex + 1}
            showDuration={showDuration}
            maxDurationMs={maxDurationMs}
          />
        )
      })}
    </div>
  )
}

function ToolCallGroup({
  calls,
  startIndex,
  turn,
  showDuration,
  maxDurationMs
}: {
  calls: ToolCallRowEntry[]
  startIndex: number
  turn: number
  showDuration: boolean
  maxDurationMs: number
}) {
  const batched = calls.length > 1
  const phases = deriveTurnPhases(calls)
  const rows = calls.map((tc, i) => (
    <SidebarToolCall
      key={tc.id}
      index={startIndex + i}
      name={tc.name}
      input={tc.input}
      output={tc.output}
      status={tc.status}
      agentKind={tc.agentKind}
      depth={tc.depth}
      turn={turn}
      batchPosition={batched ? `${i + 1}/${calls.length}` : undefined}
      durationMs={showDuration ? (tc.durationMs ?? null) : undefined}
      maxDurationMs={showDuration ? maxDurationMs : undefined}
    />
  ))

  // Nothing to add above the rows: a lone call on a surface with no harness
  // phase timing renders exactly as it did before.
  if (!batched && phases === null) return <>{rows}</>

  return (
    <div
      className={
        batched
          ? 'min-w-0 rounded-md border-l-2 border-primary bg-primary/5 py-1 pr-1 pl-1.5'
          : 'min-w-0'
      }
      role="group"
      aria-label={
        batched ? `Turn ${turn}: batched ${calls.length} tools in parallel` : `Turn ${turn}: 1 tool`
      }
    >
      {phases !== null ? (
        <TurnPhaseHeader turn={turn} callCount={calls.length} batched={batched} phases={phases} />
      ) : (
        <div className="flex items-center gap-1 pb-1 pl-0.5 text-9 font-semibold tracking-wide text-primary uppercase">
          <Zap className="size-2.5 shrink-0" aria-hidden />
          <span>
            turn {turn} · {calls.length} tools in parallel
          </span>
        </div>
      )}
      <div className="flex min-w-0 flex-col gap-1">{rows}</div>
    </div>
  )
}

const PHASE_BAR_CLASS: Record<PhaseSegment['key'], string> = {
  waiting: 'bg-st-idle',
  writing: 'bg-st-info',
  executing: 'bg-st-run'
}

/**
 * Turn-level summary above a tool batch: total wall time, a stacked bar of
 * where it went, and the numbers behind the bar on expand. Only rendered
 * for turns the harness phase-timed.
 */
function TurnPhaseHeader({
  turn,
  callCount,
  batched,
  phases
}: {
  turn: number
  callCount: number
  batched: boolean
  phases: TurnPhases
}) {
  const [expanded, setExpanded] = useState(false)
  const segments = phaseSegments(phases)
  const spoken = segments
    .map((segment) => `${segment.label} ${formatCallDurationMs(segment.ms)}`)
    .join(', ')

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} className="min-w-0">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          aria-label={`Turn ${turn} took ${formatCallDurationMs(phases.totalMs)}: ${spoken}`}
          className="flex w-full min-w-0 cursor-pointer items-center gap-1.5 rounded px-0.5 py-0.5 text-left hover:bg-muted/60"
        >
          <ChevronRight
            className={cn(
              'size-3 shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none',
              expanded && 'rotate-90'
            )}
          />
          <span
            className={`shrink-0 text-9 font-semibold tracking-wide uppercase ${
              batched ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            {batched ? (
              <span className="inline-flex items-center gap-1">
                <Zap className="size-2.5 shrink-0" aria-hidden />
                turn {turn} · {callCount} tools in parallel
              </span>
            ) : (
              `turn ${turn}`
            )}
          </span>
          <PhaseBar segments={segments} />
          <span className="shrink-0 font-mono text-9 tabular-nums text-muted-foreground">
            {formatCallDurationMs(phases.totalMs)}
          </span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <PhaseBreakdown segments={segments} phases={phases} />
      </CollapsibleContent>
    </Collapsible>
  )
}

function PhaseBar({ segments }: { segments: PhaseSegment[] }) {
  return (
    <div
      className="flex h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
      aria-hidden
      title={segments
        .map((s) => `${s.label} ${formatCallDurationMs(s.ms)} (${Math.round(s.percent)}%)`)
        .join(' · ')}
    >
      {segments.map((segment) => (
        <div
          key={segment.key}
          className={PHASE_BAR_CLASS[segment.key]}
          style={{ width: `${segment.percent}%` }}
        />
      ))}
    </div>
  )
}

function PhaseBreakdown({ segments, phases }: { segments: PhaseSegment[]; phases: TurnPhases }) {
  return (
    <div className="mb-1 ml-4 flex flex-col gap-0.5 border-l border-line pl-2 text-9">
      {segments.map((segment) => (
        <div key={segment.key} className="flex min-w-0 items-baseline gap-1.5">
          <span
            className={`size-1.5 shrink-0 translate-y-px rounded-full ${PHASE_BAR_CLASS[segment.key]}`}
            aria-hidden
          />
          <span className="w-12 shrink-0 tracking-wide text-foreground uppercase">
            {segment.label}
          </span>
          <span className="w-10 shrink-0 text-right font-mono tabular-nums text-foreground">
            {formatCallDurationMs(segment.ms)}
          </span>
          <span className="w-8 shrink-0 text-right font-mono tabular-nums text-muted-foreground">
            {Math.round(segment.percent)}%
          </span>
          <span className="min-w-0 truncate text-muted-foreground">{segment.hint}</span>
        </div>
      ))}
      {!phases.executingIsWallTime ? (
        <div className="text-muted-foreground italic">
          Tool time is summed — this turn has no recorded start times, so overlapping calls are
          counted twice.
        </div>
      ) : null}
    </div>
  )
}

export function SidebarToolCall({
  index,
  name,
  input,
  output,
  status,
  agentKind,
  depth = 0,
  turn,
  batchPosition,
  durationMs,
  maxDurationMs
}: ToolCallRowProps) {
  const [expanded, setExpanded] = useState(false)
  const hasContent = input !== undefined || output !== undefined
  const indentPx = Math.min(depth, 4) * 12
  const agentLabel = agentKind ? agentShortLabel(agentKind) : null
  const showDuration = durationMs !== undefined || maxDurationMs !== undefined
  const barWidth =
    typeof durationMs === 'number' && maxDurationMs != null && maxDurationMs > 0
      ? (durationMs / maxDurationMs) * 100
      : 0

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      className="min-w-0 overflow-hidden rounded border border-line bg-card"
      style={indentPx > 0 ? { marginLeft: indentPx } : undefined}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full min-w-0 cursor-pointer items-center gap-1.5 px-1.5 py-1 text-left text-10 hover:bg-muted/60"
        >
          <ChevronRight
            className={cn(
              'size-3 shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none',
              expanded && 'rotate-90'
            )}
          />
          <span className="font-mono text-muted-foreground">{index + 1}.</span>
          {turn !== undefined ? (
            <span
              className="shrink-0 font-mono text-9 text-muted-foreground"
              title={`Tool-calling turn ${turn}`}
            >
              T{turn}
            </span>
          ) : null}
          <span className="min-w-0 flex-1 truncate font-mono text-foreground">{name}</span>
          {batchPosition ? (
            <span className="shrink-0 rounded-sm bg-primary/15 px-1 py-px font-mono text-9 text-primary">
              {batchPosition}
            </span>
          ) : null}
          {agentLabel ? (
            <span className="shrink-0 rounded-sm bg-muted px-1 py-px text-9 tracking-wide text-muted-foreground uppercase">
              {agentLabel}
            </span>
          ) : null}
          {showDuration ? (
            <>
              <div className="hidden h-1 w-12 overflow-hidden rounded-full bg-muted sm:block">
                <div
                  className="h-full rounded-full bg-muted-foreground/50"
                  style={{ width: `${barWidth}%` }}
                />
              </div>
              <span
                className="min-w-10 shrink-0 text-right font-mono text-9 tabular-nums text-muted-foreground"
                aria-label={
                  typeof durationMs === 'number'
                    ? `Took ${formatCallDurationMs(durationMs)}`
                    : 'Duration unknown'
                }
              >
                {typeof durationMs === 'number' ? formatCallDurationMs(durationMs) : '—'}
              </span>
            </>
          ) : null}
          {status ? <StatusGlyph status={status} /> : null}
        </button>
      </CollapsibleTrigger>

      {hasContent ? (
        <CollapsibleContent>
          <div className="flex min-w-0 flex-col gap-1.5 border-t border-line px-1.5 py-1.5 text-10">
            {input !== undefined ? <PayloadBlock label="input" value={input} /> : null}
            {output !== undefined ? (
              SUBGRAPH_TOOL_NAMES.has(name) ? (
                <SubgraphSvgBlock value={output} />
              ) : (
                <PayloadBlock label="output" value={output} />
              )
            ) : null}
          </div>
        </CollapsibleContent>
      ) : null}
    </Collapsible>
  )
}

function SubgraphSvgBlock({ value }: { value: unknown }) {
  if (value === null || typeof value !== 'object') {
    return <PayloadBlock label="output" value={value} />
  }
  const v = value as {
    svg?: unknown
    nodeCount?: unknown
    edgeCount?: unknown
    note?: unknown
    title?: unknown
    label?: unknown
  }
  const svg = typeof v.svg === 'string' ? v.svg : null
  if (!svg) return <PayloadBlock label="output" value={value} />
  const sanitized = sanitizeSubgraphSvg(svg)
  const counts =
    typeof v.nodeCount === 'number' && typeof v.edgeCount === 'number'
      ? `${v.nodeCount} node${v.nodeCount === 1 ? '' : 's'}, ${v.edgeCount} edge${v.edgeCount === 1 ? '' : 's'}`
      : null
  const note = typeof v.note === 'string' ? v.note : null
  const title =
    typeof v.title === 'string' && v.title.trim().length > 0
      ? v.title.trim()
      : typeof v.label === 'string' && v.label.trim().length > 0
        ? v.label.trim()
        : 'ingest subgraph'
  return (
    <div className="min-w-0">
      <div className="mb-0.5 text-9 tracking-wide text-muted-foreground uppercase">
        {title.toLowerCase()}
        {counts ? ` — ${counts}` : ''}
      </div>
      <div
        className="overflow-auto rounded bg-muted/40 px-1.5 py-1"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
      {note ? <div className="mt-1 text-10 text-muted-foreground italic">{note}</div> : null}
    </div>
  )
}

function StatusGlyph({ status }: { status: 'running' | 'completed' | 'error' }) {
  if (status === 'completed') {
    return (
      <span className="shrink-0 font-mono text-st-run" aria-label="completed">
        ✓
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span className="shrink-0 font-mono text-destructive" aria-label="error">
        !
      </span>
    )
  }
  return (
    <span className="shrink-0 font-mono text-muted-foreground" aria-label="running">
      …
    </span>
  )
}

function PayloadBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="min-w-0">
      <div className="mb-0.5 text-9 tracking-wide text-muted-foreground uppercase">{label}</div>
      <pre className="max-h-48 w-full min-w-0 overflow-auto rounded bg-muted/40 px-1.5 py-1 font-mono text-10 break-all whitespace-pre-wrap text-foreground">
        {formatPayload(value)}
      </pre>
    </div>
  )
}
